import { type ScopedThreadRef } from "@cmmd-forge/contracts";
import { useIsMutating, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { type PendingDefaultBranchAction, useGitActionRunner } from "./GitActionsControl.runner";
import { useGitThreadBranchSync } from "./GitActionsControl.threadSync";
import { useGitActionsControlHandlers } from "./GitActionsControl.handlers";
import { useGitCommitDialogActions } from "./GitActionsControl.dialogActions";
import { useGitStatusWindowRefresh } from "./GitActionsControl.effects";
import { useGitFileSelectionActions } from "./GitActionsControl.fileSelection";
import { useGitActionsControlModel } from "./GitActionsControl.model";
import { GitActionsControlView } from "./GitActionsControlView";
import {
  gitInitMutationOptions,
  gitMutationKeys,
  gitPullMutationOptions,
  gitRunStackedActionMutationOptions,
} from "~/lib/gitReactQuery";
import { refreshGitStatus, useGitStatus } from "~/lib/gitStatusState";
import { useComposerDraftStore } from "~/composerDraftStore";
import type { DraftId } from "~/draftId";
import { useStore } from "~/store";
import { createThreadSelectorByRef } from "~/storeSelectors";
import { useUiStateStore } from "~/uiStateStore";

interface GitActionsControlProps {
  readonly gitCwd: string | null;
  readonly activeThreadRef: ScopedThreadRef | null;
  readonly draftId?: DraftId;
  readonly compact?: boolean;
}

export default function GitActionsControl({
  gitCwd,
  activeThreadRef,
  draftId,
  compact = false,
}: GitActionsControlProps) {
  const activeEnvironmentId = activeThreadRef?.environmentId ?? null;
  const threadToastData = useMemo(
    () => (activeThreadRef ? { threadRef: activeThreadRef } : undefined),
    [activeThreadRef],
  );
  const activeServerThreadSelector = useMemo(
    () => createThreadSelectorByRef(activeThreadRef),
    [activeThreadRef],
  );
  const activeServerThread = useStore(activeServerThreadSelector);
  const activeDraftThread = useComposerDraftStore((store) =>
    draftId
      ? store.getDraftSession(draftId)
      : activeThreadRef
        ? store.getDraftThreadByRef(activeThreadRef)
        : null,
  );
  const setDraftThreadContext = useComposerDraftStore((store) => store.setDraftThreadContext);
  const setThreadBranch = useStore((store) => store.setThreadBranch);
  const setThreadLinkedPr = useUiStateStore((state) => state.setThreadLinkedPr);
  const queryClient = useQueryClient();
  const [isCommitDialogOpen, setIsCommitDialogOpen] = useState(false);
  const [dialogCommitMessage, setDialogCommitMessage] = useState("");
  const [excludedFiles, setExcludedFiles] = useState<ReadonlySet<string>>(new Set());
  const [isEditingFiles, setIsEditingFiles] = useState(false);
  const [pendingDefaultBranchAction, setPendingDefaultBranchAction] =
    useState<PendingDefaultBranchAction | null>(null);

  const { data: gitStatus = null, error: gitStatusError } = useGitStatus({
    environmentId: activeEnvironmentId,
    cwd: gitCwd,
  });
  const gitStatusForActions = gitStatus;

  const initMutation = useMutation(
    gitInitMutationOptions({ environmentId: activeEnvironmentId, cwd: gitCwd, queryClient }),
  );

  const runImmediateGitActionMutation = useMutation(
    gitRunStackedActionMutationOptions({
      environmentId: activeEnvironmentId,
      cwd: gitCwd,
      queryClient,
      threadId: activeThreadRef?.threadId ?? activeDraftThread?.threadId ?? null,
    }),
  );
  const pullMutation = useMutation(
    gitPullMutationOptions({ environmentId: activeEnvironmentId, cwd: gitCwd, queryClient }),
  );

  const isRunStackedActionRunning =
    useIsMutating({
      mutationKey: gitMutationKeys.runStackedAction(activeEnvironmentId, gitCwd),
    }) > 0;
  const isPullRunning =
    useIsMutating({ mutationKey: gitMutationKeys.pull(activeEnvironmentId, gitCwd) }) > 0;
  const isGitActionRunning = isRunStackedActionRunning || isPullRunning;
  const isSelectingWorktreeBase =
    activeDraftThread?.envMode === "worktree" && activeDraftThread.worktreePath === null;

  const { syncThreadBranchAfterGitAction } = useGitThreadBranchSync({
    activeThreadRef,
    draftId,
    activeServerThread,
    activeDraftThread,
    gitStatus: gitStatusForActions,
    isGitActionRunning,
    isSelectingWorktreeBase,
    setThreadBranch,
    setDraftThreadContext,
  });

  const model = useGitActionsControlModel({
    gitStatus: gitStatusForActions,
    excludedFiles,
    isGitActionRunning,
    pendingDefaultBranchAction,
  });

  useGitStatusWindowRefresh({ activeEnvironmentId, gitCwd });

  const runGitActionWithToast = useGitActionRunner({
    activeThreadRef,
    gitCwd,
    gitStatus: gitStatusForActions,
    isDefaultBranch: model.isDefaultBranch,
    threadToastData,
    runMutation: runImmediateGitActionMutation,
    setPendingDefaultBranchAction,
    syncThreadBranchAfterGitAction,
    setThreadLinkedPr,
  });

  const openCommitDialog = useCallback(() => {
    setExcludedFiles(new Set());
    setIsEditingFiles(false);
    setIsCommitDialogOpen(true);
  }, []);
  const {
    runQuickAction,
    openMenuItem,
    openChangedFile: openChangedFileInEditor,
  } = useGitActionsControlHandlers({
    gitCwd,
    gitStatus: gitStatusForActions,
    quickAction: model.quickAction,
    threadToastData,
    pullMutation,
    runGitActionWithToast,
    openCommitDialog,
  });

  const {
    resetCommitDialog,
    continuePendingDefaultBranchAction,
    checkoutFeatureBranchAndContinuePendingAction,
    runDialogActionOnNewBranch,
    runDialogAction,
  } = useGitCommitDialogActions({
    isCommitDialogOpen,
    dialogCommitMessage,
    allSelected: model.allSelected,
    selectedFiles: model.selectedFiles,
    pendingDefaultBranchAction,
    runGitActionWithToast,
    setIsCommitDialogOpen,
    setDialogCommitMessage,
    setExcludedFiles,
    setIsEditingFiles,
    setPendingDefaultBranchAction,
  });
  const { toggleEditingFiles, toggleAllFiles, toggleFile } = useGitFileSelectionActions({
    allFiles: model.allFiles,
    allSelected: model.allSelected,
    setExcludedFiles,
    setIsEditingFiles,
  });

  if (!gitCwd) return null;

  return (
    <GitActionsControlView
      compact={compact}
      gitStatus={gitStatusForActions}
      gitStatusErrorMessage={gitStatusError?.message ?? null}
      hasOriginRemote={model.hasOriginRemote}
      isDefaultBranch={model.isDefaultBranch}
      isGitActionRunning={isGitActionRunning}
      isRepo={model.isRepo}
      isInitPending={initMutation.isPending}
      quickAction={model.quickAction}
      quickActionDisabledReason={model.quickActionDisabledReason}
      gitActionMenuItems={model.gitActionMenuItems}
      allFiles={model.allFiles}
      selectedFiles={model.selectedFiles}
      excludedFiles={excludedFiles}
      allSelected={model.allSelected}
      noneSelected={model.noneSelected}
      isEditingFiles={isEditingFiles}
      isCommitDialogOpen={isCommitDialogOpen}
      dialogCommitMessage={dialogCommitMessage}
      pendingDefaultBranchActionOpen={pendingDefaultBranchAction !== null}
      pendingDefaultBranchActionCopy={model.pendingDefaultBranchActionCopy}
      onInitializeGit={() => initMutation.mutate()}
      onRefreshGitStatus={() => {
        void refreshGitStatus({ environmentId: activeEnvironmentId, cwd: gitCwd }).catch(
          () => undefined,
        );
      }}
      onRunQuickAction={runQuickAction}
      onOpenMenuItem={openMenuItem}
      onCommitDialogOpenChange={(open) => {
        if (!open) resetCommitDialog();
      }}
      onDialogCommitMessageChange={setDialogCommitMessage}
      onToggleEditingFiles={toggleEditingFiles}
      onToggleAllFiles={toggleAllFiles}
      onToggleFile={toggleFile}
      onOpenChangedFile={openChangedFileInEditor}
      onRunDialogActionOnNewBranch={runDialogActionOnNewBranch}
      onRunDialogAction={runDialogAction}
      onDefaultBranchDialogOpenChange={(open) => {
        if (!open) {
          setPendingDefaultBranchAction(null);
        }
      }}
      onContinuePendingDefaultBranchAction={continuePendingDefaultBranchAction}
      onCheckoutFeatureBranchAndContinue={checkoutFeatureBranchAndContinuePendingAction}
    />
  );
}
